import { useState, useEffect, useCallback, useRef } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useDispatchApp, useSelectorApp } from "../../../../lib/redux";
import { getPathDatabaseByRole } from "../../../../actions/auth";
import {
  startGetBrandInformation,
  startSaveBrandInformationFromUrl,
  startExtractBrandInformationFromUrl,
  startDetectAndSaveBrandLanguage,
  startClassifyAdvertiserSeasonalEvents,
  startAnalyzeBusinessProductImages,
} from "../../../../actions/adsCampaigns";
import { startUploadFileAws } from "../../../../services/public";
import {
  startGetBrandSetupVersions,
  startSaveBrandSetupVersion,
  startGetBrandSetupVersionSnapshot,
} from "../../../../actions/brandSetupVersions";
import {
  startGetBusinessName,
  startGetAdsUsers,
} from "../../../../actions/user";
import {
  ConfirmAlert,
  SimpleAlert,
} from "../../../../components/Alerts/Alerts";
import {
  ALERT_ICON_TYPE_SUCCESS,
  ALERT_ICON_TYPE_WARNING,
} from "../../../../utils/constants";
import {
  READY,
  MODAL_BRAND_SETUP_TITLE,
  MODAL_BRAND_SETUP_DESCRIPTION,
  MODAL_BRAND_SETUP_CONFIRM_TITLE,
  MODAL_BRAND_SETUP_CONFIRM_DESCRIPTION,
  MODAL_BRAND_SETUP_CONFIRM_BUTTON,
  MODAL_BRAND_SETUP_REVIEW_BUTTON,
  FIELD_REQUIRED,
  WARNING,
  BRAND_SETUP_EXTRACTION_WARNING_MESSAGE,
  BRAND_SETUP_GENERATION_PDF_INVALID,
  BRAND_SETUP_GENERATION_PDF_TOO_LARGE,
  ERROR_OCCURRED,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import useFocusErrorForm from "../../../../hooks/useFocusErrorForm";
import { LaunchError } from "../../../../utils/errors";
import { getValidationRules } from "../utils/validationRules";
import {
  getDefaultFormValues,
  someImportantDataExtractedIsEmpty,
} from "../utils/formDataTransformers";
import {
  normalizeApiDataToForm,
  normalizeFormDataToApi,
  mergeBusinessProductImagesOnSave,
  needsProductImageMetadata,
  preserveFormTypography,
} from "../utils/formDataTransformers";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import UsersTable from "../../../../pages/Users/components/UsersTable";
import { validateBrandSetupTextFields } from "../utils/blockedBrandsValidation";
import { getBrandSetupChangedFields } from "../utils/brandSetupDiff";
import { startInvalidateCompetitorAdsCache } from "../../../../actions/competitorAdsFavorites";
import {
  BRAND_SOURCE_PDF_ERROR_SIZE,
  BRAND_SOURCE_PDF_MIME,
  MAX_BRAND_SOURCE_PDF_MB,
  getBrandSourcePdfError,
} from "../utils/brandSourcePdf";

export const useBrandSetup = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { userID } = dispatch(getPathDatabaseByRole());

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isReExtracting, setIsReExtracting] = useState(false);
  const [isExtracted, setIsExtracted] = useState(false);
  const [firstExtracting, setFirstExtracting] = useState(false);
  const [usedManualOption, setUsedManualOption] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [users, setUsers] = useState({});
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [versions, setVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [selectedVersionID, setSelectedVersionID] = useState(null);
  const [
    hasAdvancedConfigurationAdditionalAssets,
    setHasAdvancedConfigurationAdditionalAssets,
  ] = useState(false);
  const [hasAdditionalAssets, setHasAdditionalAssets] = useState(false);
  const [requiresTypographicFidelity, setRequiresTypographicFidelity] =
    useState(false);
  const dbProductImagesRef = useRef([]);
  const lastSavedDataRef = useRef(null);
  const shouldInvalidateCompetitorsCacheRef = useRef(false);
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [generationPdf, setGenerationPdf] = useState(null);
  const [generationError, setGenerationError] = useState("");

  const { user } = useSelectorApp((state) => state.auth);
  const isAnonymous = user?.isAnonymous;
  const isPlanActive = useSelectorApp((state) => state.payments.isPlanActive);
  const isTrial = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.isTrial
  );

  const adsCampaignsIDsByAgency = useSelectorApp(
    (state) => state.adsCampaigns.adsCampaignsIDsByAgency
  );
  const totalCampaignsCreated = adsCampaignsIDsByAgency?.length || 0;

  const formMethods = useForm({
    defaultValues: getDefaultFormValues(),
    mode: "onChange",
  });

  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    formState,
    handleSubmit,
    trigger,
    setError,
  } = formMethods;

  const {
    append: appendBusinessProductImages,
    remove: removeBusinessProductImage,
  } = useFieldArray({
    control,
    name: "businessProductImages",
  });

  const {
    fields: additionalAssetsFields,
    append: appendAdditionalAsset,
    remove: removeAdditionalAsset,
    update: updateAdditionalAsset,
  } = useFieldArray({
    control,
    name: "additionalAssets",
  });

  const {
    fields: businessCompetitorsFields,
    append: appendBusinessCompetitors,
    remove: removeBusinessCompetitors,
  } = useFieldArray({
    control,
    name: "businessCompetitors",
  });

  const {
    fields: socialNetworksFields,
    append: appendSocialNetworks,
    remove: removeSocialNetworks,
  } = useFieldArray({
    control,
    name: "socialNetworks",
  });

  useFocusErrorForm(formState);
  const validationRules = getValidationRules(t);

  const loadInitialData = useCallback(async () => {
    reset();
    setIsLoading(true);
    setIsLoadingVersions(true);
    setSelectedVersionID(null);
    try {
      const [response, versionsResponse] = await Promise.all([
        dispatch(startGetBrandInformation()),
        dispatch(startGetBrandSetupVersions()),
      ]);

      if (response.ok) {
        const { data } = response;
        setHasAdvancedConfigurationAdditionalAssets(
          data?.hasAdvancedConfigurationAdditionalAssets === true
        );
        setHasAdditionalAssets(data?.hasAdditionalAssets === true);
        setRequiresTypographicFidelity(
          data?.requiresTypographicFidelity === true
        );
        const emptyDefaultFontFamily =
          data?.requiresTypographicFidelity === true;
        const normalizedData = normalizeApiDataToForm(data, {
          emptyDefaultFontFamily,
        });
        reset(normalizedData);
        dbProductImagesRef.current = normalizedData.businessProductImages || [];
        lastSavedDataRef.current = normalizeFormDataToApi(normalizedData, {
          emptyDefaultFontFamily,
        });
        if (
          data?.businessDescription ||
          data?.businessTextStyle ||
          data?.businessCompetitors ||
          data?.socialNetworks ||
          data?.websiteUrl
        ) {
          setIsExtracted(true);
          setFirstExtracting(true);
        }
      }

      if (versionsResponse.ok) {
        setVersions(versionsResponse.data || []);
      }
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "useBrandSetup",
          capture: "exception",
        })
      );
      console.error("Error loading brand information:", error);
    } finally {
      setIsLoadingVersions(false);
    }
    setIsLoadingUsers(true);
    try {
      const responseUsers = await dispatch(
        startGetAdsUsers({
          onCallbackLoadingFinish: () => setIsLoading(false),
        })
      );
      setUsers(responseUsers || {});
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "useBrandSetup",
          capture: "exception",
        })
      );
      console.error("Error loading users:", error);
    } finally {
      setIsLoadingUsers(false);
      setIsLoading(false);
    }
  }, [dispatch, reset]);

  const onGenerationPromptChange = useCallback(({ value }) => {
    setGenerationPrompt(value);
  }, []);

  const onGenerationPdfSelected = useCallback(
    ({ file }) => {
      const error = getBrandSourcePdfError({ file });
      if (error) {
        setGenerationError(
          error === BRAND_SOURCE_PDF_ERROR_SIZE
            ? t(BRAND_SETUP_GENERATION_PDF_TOO_LARGE, {
                maxMb: MAX_BRAND_SOURCE_PDF_MB,
              })
            : t(BRAND_SETUP_GENERATION_PDF_INVALID)
        );
        return;
      }
      setGenerationError("");
      setGenerationPdf(file);
    },
    [t]
  );

  const onGenerationPdfRemoved = useCallback(() => {
    setGenerationPdf(null);
    setGenerationError("");
  }, []);

  // Optional brief sent along with the website analysis. Returns null when
  // the PDF could not be uploaded, so callers can abort the extraction.
  const buildExtractionOptions = useCallback(async () => {
    const options = {
      skipTypographyPreview: requiresTypographicFidelity,
      prompt: generationPrompt.trim(),
    };
    if (!generationPdf) return options;
    const fileUrl = await dispatch(
      startUploadFileAws(
        generationPdf,
        BRAND_SOURCE_PDF_MIME,
        `brand-setup-sources/${userID}/`,
        true,
        true
      )
    );
    return fileUrl ? { ...options, fileUrl } : null;
  }, [
    dispatch,
    generationPdf,
    generationPrompt,
    requiresTypographicFidelity,
    userID,
  ]);

  const handleReExtract = useCallback(
    async (url) => {
      if (!url) return;
      if (isExtracted && !usedManualOption && !isAnonymous) {
        const confirm = await ConfirmAlert({
          title: t(MODAL_BRAND_SETUP_TITLE),
          width: "min(560px, 92vw)",
          html: (
            <Box
              sx={{
                width: "100%",
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: 0,
              }}
            >
              <Typography variant="body1" sx={{ flexShrink: 0 }}>
                {t(MODAL_BRAND_SETUP_DESCRIPTION)}
              </Typography>
              <Box
                sx={{
                  maxHeight: 320,
                  minWidth: 0,
                  width: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
              >
                <UsersTable
                  data={users || {}}
                  loading={isLoadingUsers}
                  showEmail={false}
                  showActions={false}
                  showRole={true}
                  filterByAdvertiserAssigned={userID}
                  stickyHeader={false}
                />
              </Box>
            </Box>
          ),
        });
        if (!confirm.isConfirmed) {
          return;
        }
      }
      const currentUrl = url || getValues("websiteUrl");
      if (!currentUrl) {
        setError("websiteUrl", {
          message: t(FIELD_REQUIRED),
        });
        return;
      }
      const isValid = await trigger("websiteUrl");
      if (!isValid) {
        return;
      }
      setIsReExtracting(true);
      setIsExtracting(true);
      try {
        const options = await buildExtractionOptions();
        if (!options) return;
        const response = await dispatch(
          startExtractBrandInformationFromUrl(currentUrl, options)
        );
        const currentTypography = {
          businessTypography: getValues("businessTypography"),
          businessTypographyPreview:
            getValues("businessTypographyPreview") || "",
        };
        if (response.ok) {
          let normalized = normalizeApiDataToForm(response.data, {
            emptyDefaultFontFamily: requiresTypographicFidelity,
          });
          if (requiresTypographicFidelity) {
            normalized = preserveFormTypography(normalized, currentTypography);
          }
          const isImportantDataEmpty =
            someImportantDataExtractedIsEmpty(normalized);
          if (isImportantDataEmpty) {
            SimpleAlert({
              title: t(WARNING),
              text: t(BRAND_SETUP_EXTRACTION_WARNING_MESSAGE),
              icon: ALERT_ICON_TYPE_WARNING,
            });
          }
          reset(normalized);
          setIsExtracted(true);
          shouldInvalidateCompetitorsCacheRef.current = true;
        } else {
          const defaultValues = getDefaultFormValues({
            emptyDefaultFontFamily: requiresTypographicFidelity,
          });
          const nextValues = {
            ...defaultValues,
            websiteUrl: currentUrl,
          };
          reset(
            requiresTypographicFidelity
              ? preserveFormTypography(nextValues, currentTypography)
              : nextValues
          );
        }
      } catch (error) {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "useBrandSetup",
            capture: "exception",
          })
        );
        console.error("Error re-extracting brand information:", error);
      } finally {
        setIsReExtracting(false);
        setIsExtracting(false);
      }
    },
    [
      dispatch,
      buildExtractionOptions,
      getValues,
      reset,
      trigger,
      setError,
      isExtracted,
      usedManualOption,
      users,
      isLoadingUsers,
      userID,
      t,
      isAnonymous,
      requiresTypographicFidelity,
    ]
  );

  const getBusinessNameLowercase = (businessName) =>
    (businessName || "").toLowerCase().trim();

  const saveFormData = useCallback(
    async (formData) => {
      const confirm = await ConfirmAlert({
        title: t(MODAL_BRAND_SETUP_CONFIRM_TITLE),
        text: t(MODAL_BRAND_SETUP_CONFIRM_DESCRIPTION),
        confirmButtonText: t(MODAL_BRAND_SETUP_CONFIRM_BUTTON),
        cancelButtonText: t(MODAL_BRAND_SETUP_REVIEW_BUTTON),
      });
      if (!confirm.isConfirmed) {
        return { success: false, cancelled: true };
      }
      setIsSaving(true);
      let submitData = {
        ...formData,
        businessProductImages:
          getValues("businessProductImages") ||
          formData.businessProductImages ||
          [],
      };
      if (!isExtracted && !usedManualOption && !formData.websiteUrl) {
        setError("websiteUrl", {
          message: t(FIELD_REQUIRED),
        });
        setIsSaving(false);
        return { success: false, error: "URL required" };
      }
      if (!isExtracted && !usedManualOption && formData.websiteUrl) {
        const isValid = await trigger("websiteUrl");
        if (!isValid) {
          setIsSaving(false);
          return { success: false, error: "Invalid URL" };
        }
        setIsExtracting(true);
        const options = await buildExtractionOptions();
        if (!options) {
          setIsExtracting(false);
          setIsSaving(false);
          return { success: false, error: "Brief upload failed" };
        }
        const extractResponse = await dispatch(
          startExtractBrandInformationFromUrl(formData.websiteUrl, options)
        );
        if (extractResponse.ok) {
          const currentTypography = {
            businessTypography: getValues("businessTypography"),
            businessTypographyPreview:
              getValues("businessTypographyPreview") || "",
          };
          const {
            businessName = "",
            businessDescription = "",
            businessTextStyle = "",
            businessLookAndFeel = "",
            businessValueProposition = "",
            businessOffering = "",
            businessIdealCustomerProfile = "",
            businessCountry = "",
            businessColors = [],
            businessCompetitors = [],
            logoUrl = "",
            socialNetworks = [],
          } = extractResponse.data;
          const businessBuyerPerson =
            extractResponse.data?.businessBuyerPerson ??
            extractResponse.data?.businessBuyerPerson ??
            [];
          const extractedData = {
            businessName,
            businessDescription,
            businessTextStyle,
            businessLookAndFeel,
            businessValueProposition,
            businessOffering,
            businessIdealCustomerProfile,
            businessBuyerPerson,
            businessCountry,
            businessColors,
            businessCompetitors:
              businessCompetitors.length > 0 ? businessCompetitors : [],
            logoUrl,
            socialNetworks: socialNetworks.length > 0 ? socialNetworks : [],
          };
          let payload = { ...getValues(), ...extractedData };
          if (requiresTypographicFidelity) {
            payload = preserveFormTypography(payload, currentTypography);
          }
          reset(payload);
          submitData = payload;
        }
        setIsExtracting(false);
        setIsExtracted(true);
      }
      submitData.businessNameLowercase = getBusinessNameLowercase(
        submitData.businessName
      );
      if (!submitData.businessColors) {
        submitData.businessColors = getValues("businessColors") || [];
      }
      submitData.businessProductImages =
        getValues("businessProductImages") ||
        submitData.businessProductImages ||
        [];
      submitData.businessProductImages = mergeBusinessProductImagesOnSave({
        formItems: submitData.businessProductImages || [],
        dbItems: dbProductImagesRef.current,
      });
      try {
        validateBrandSetupTextFields({
          formData: submitData,
          isPlanActive,
          isTrial,
        });
        const apiData = normalizeFormDataToApi(submitData, {
          emptyDefaultFontFamily: requiresTypographicFidelity,
        });
        const shouldAnalyzeProductImages = needsProductImageMetadata(
          apiData.businessProductImages
        );
        const response = await dispatch(
          startSaveBrandInformationFromUrl(apiData)
        );
        if (response.ok) {
          const changedFields = getBrandSetupChangedFields({
            currentData: apiData,
            previousData: lastSavedDataRef.current,
          });
          const shouldInvalidateCompetitorsCache =
            shouldInvalidateCompetitorsCacheRef.current ||
            changedFields.includes("businessName") ||
            changedFields.includes("businessCompetitors");

          if (shouldInvalidateCompetitorsCache) {
            await dispatch(startInvalidateCompetitorAdsCache());
            shouldInvalidateCompetitorsCacheRef.current = false;
          }

          dbProductImagesRef.current = apiData.businessProductImages || [];
          const source = selectedVersionID ? "restore" : "manual_save";
          const versionResult = await dispatch(
            startSaveBrandSetupVersion({
              apiData,
              previousApiData: lastSavedDataRef.current,
              source,
            })
          );

          if (versionResult?.ok && !versionResult?.skipped) {
            lastSavedDataRef.current = apiData;
            const versionsResponse = await dispatch(
              startGetBrandSetupVersions()
            );
            if (versionsResponse.ok) {
              setVersions(versionsResponse.data || []);
            }
            setSelectedVersionID(null);
          } else if (versionResult?.ok && versionResult?.skipped) {
            lastSavedDataRef.current = apiData;
          }

          dispatch(startDetectAndSaveBrandLanguage());
          dispatch(startClassifyAdvertiserSeasonalEvents());
          if (shouldAnalyzeProductImages) {
            void dispatch(startAnalyzeBusinessProductImages());
          }
          await dispatch(startGetBusinessName());
          if (!firstExtracting && totalCampaignsCreated === 0) {
            if (isAnonymous) {
              return { success: true, showCreateAccountModal: true };
            } else {
              return { success: true, showSuccessModal: true };
            }
          } else {
            SimpleAlert({
              title: t(READY),
              icon: ALERT_ICON_TYPE_SUCCESS,
            });
            return { success: true };
          }
        }
        return { success: false, error: response.error };
      } catch (error) {
        await dispatch(
          LaunchError(error, false, [], {
            functionName: "saveFormData",
            capture: "exception",
          })
        );
        const processedError = await dispatch(
          LaunchError(
            {
              code: ERROR_OCCURRED,
              message: t(ERROR_OCCURRED),
            },
            true,
            [],
            {
              functionName: "saveFormData",
              skipSentry: true,
            }
          )
        );
        return { success: false, error: processedError || error };
      } finally {
        setIsSaving(false);
      }
    },
    [
      dispatch,
      buildExtractionOptions,
      t,
      isExtracted,
      usedManualOption,
      trigger,
      setError,
      getValues,
      reset,
      firstExtracting,
      totalCampaignsCreated,
      isAnonymous,
      isPlanActive,
      isTrial,
      selectedVersionID,
      requiresTypographicFidelity,
    ]
  );

  const onSelectVersion = useCallback(
    async ({ versionID }) => {
      if (!versionID) {
        const response = await dispatch(startGetBrandInformation());
        if (response.ok) {
          const normalizedData = normalizeApiDataToForm(response.data, {
            emptyDefaultFontFamily: requiresTypographicFidelity,
          });
          reset(normalizedData);
          dbProductImagesRef.current =
            normalizedData.businessProductImages || [];
        }
        setSelectedVersionID(null);
        return;
      }

      const snapshotResponse = await dispatch(
        startGetBrandSetupVersionSnapshot({ versionID })
      );

      if (snapshotResponse.ok) {
        const normalizedData = normalizeApiDataToForm(snapshotResponse.data, {
          emptyDefaultFontFamily: requiresTypographicFidelity,
        });
        reset(normalizedData);
        dbProductImagesRef.current = normalizedData.businessProductImages || [];
        setSelectedVersionID(versionID);
      }
    },
    [dispatch, reset, requiresTypographicFidelity]
  );

  const handleNotHaveWebsite = useCallback(() => {
    setError("websiteUrl", {});
    setValue("websiteUrl", "");
    setValue("businessName", "");
    setValue("businessDescription", "");
    setValue("businessTextStyle", "");
    setValue("businessLookAndFeel", "");
    setValue("businessValueProposition", "");
    setValue("businessOffering", "");
    setValue("businessIdealCustomerProfile", "");
    setValue("businessBuyerPerson", []);
    setValue("businessColors", []);
    setValue("businessCompetitors", []);
    setValue("logoUrl", "");
    setValue("businessProductImages", []);
    setValue("additionalAssets", []);
    setValue("businessCountry", "");
    setValue("socialNetworks", []);
    dbProductImagesRef.current = [];
    setIsExtracted(true);
    setIsExtracting(false);
    setUsedManualOption(true);
    trigger("websiteUrl");
  }, [setError, setValue, trigger]);

  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  return {
    control,
    errors: formState.errors,
    watch,
    setValue,
    getValues,
    reset,
    formState,
    handleSubmit,
    trigger,
    setError,
    validationRules,
    businessCompetitorsFields,
    appendBusinessCompetitors,
    removeBusinessCompetitors,
    socialNetworksFields,
    appendSocialNetworks,
    removeSocialNetworks,
    isLoading,
    isSaving,
    isReExtracting,
    isExtracted,
    firstExtracting,
    usedManualOption,
    isExtracting,
    users,
    isLoadingUsers,
    handleReExtract,
    generationSources: {
      prompt: generationPrompt,
      pdfFile: generationPdf,
      errorMessage: generationError,
      onPromptChange: onGenerationPromptChange,
      onPdfSelected: onGenerationPdfSelected,
      onPdfRemoved: onGenerationPdfRemoved,
    },
    saveFormData,
    handleNotHaveWebsite,
    appendBusinessProductImages,
    removeBusinessProductImage,
    additionalAssetsFields,
    appendAdditionalAsset,
    removeAdditionalAsset,
    updateAdditionalAsset,
    versions,
    isLoadingVersions,
    selectedVersionID,
    onSelectVersion,
    hasAdvancedConfigurationAdditionalAssets,
    hasAdditionalAssets,
    requiresTypographicFidelity,
  };
};
